import { useEffect } from "react";
import { Link } from "react-router-dom";
import { BarChart3, EyeOff, FlaskConical, GitCompareArrows, ShieldCheck } from "lucide-react";
import { useComparisonsStore } from "@/stores/comparisons";
import { Card, CardContent } from "@/components/ui/card";
import { HistoryTable } from "@/components/history/HistoryTable";
import { Spinner } from "@/components/ui/spinner";
import { ErrorState } from "@/components/ui/error-state";

const RECENT_LIMIT = 8;

const tools = [
  {
    to: "/app/compare",
    icon: GitCompareArrows,
    title: "Compare brands",
    body: "Run any brands through any prompt right now and see scores side by side.",
    cta: "Run a comparison",
  },
  {
    to: "/app/blind",
    icon: EyeOff,
    title: "Blind queries",
    body: "See who AI surfaces when nobody names your brands — share of voice, new names, recent runs.",
    cta: "See who AI surfaces",
  },
  {
    to: "/app/studies",
    icon: FlaskConical,
    title: "Studies",
    body: "Run a full multi-model study — once, or on a schedule of its own.",
    cta: "Open studies",
  },
  {
    to: "/app/insights",
    icon: BarChart3,
    title: "Insights",
    body: "Win rates and score trends across everything you've run.",
    cta: "View insights",
  },
];

export function ExploreView() {
  const { history, loading, error, fetchHistory } = useComparisonsStore();

  useEffect(() => {
    fetchHistory();
  }, []);

  return (
    <div className="space-y-5">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 stagger-children">
        {tools.map((tool) => (
          <Link key={tool.to} to={tool.to} className="group">
            <Card className="h-full transition-colors group-hover:border-brand-100">
              <CardContent>
                <div className="w-8 h-8 rounded-lg bg-brand-50 flex items-center justify-center text-brand-600">
                  <tool.icon className="w-4 h-4" />
                </div>
                <h3 className="mt-3 text-[13.5px] font-bold text-brand-950">{tool.title}</h3>
                <p className="mt-1 text-[12px] text-slate-500">{tool.body}</p>
                <span className="mt-3 inline-block text-[12px] font-bold text-brand-600">{tool.cta} →</span>
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>

      <Card>
        <CardContent>
          <div className="flex items-center justify-between mb-3">
            <span className="text-[11px] font-semibold tracking-[0.08em] uppercase text-slate-500">Recent activity</span>
            <Link to="/app/history" className="text-[12px] font-medium text-brand-600 hover:underline">
              View all activity →
            </Link>
          </div>
          {loading ? (
            <Spinner message="Loading activity…" />
          ) : error ? (
            <ErrorState message={error} onRetry={fetchHistory} />
          ) : (
            <HistoryTable comparisons={history.slice(0, RECENT_LIMIT)} />
          )}
        </CardContent>
      </Card>

      <div className="flex items-start gap-2.5 rounded-xl border border-brand-100 bg-brand-50/60 px-4 py-3 text-[12.5px] text-brand-700">
        <ShieldCheck className="w-4 h-4 shrink-0 mt-0.5" />
        <span>
          <span className="font-bold">Experiments don't skew your dashboard.</span>{" "}
          When weekly tracking is on, Brand Health reads only your tracked runs — everything here is
          recorded to activity but stays out of the headline numbers.
        </span>
      </div>
    </div>
  );
}
